import Quill from "quill";

import {
  FORMAT_TYPES,
  ASSIST_FORMAT_TYPES,
  CUSTOM_BLOCK_TYPES,
  createTextNode,
  createElement,
  setAttribute,
  GROUP_TYPES,
  ASSIST_GROUP_TYPE,
  ATTRIBUTE_ORDER
} from "../index.js";

const Delta = Quill.import("delta");

/**
 * Convert a delta value to HTML.
 *
 * This function takes a delta value, typically obtained from a rich text editor
 * like Quill.js, and converts it to HTML format, preserving the formatting and
 * structure of the original content.
 *
 * @param {Array} delta - An array of delta operations representing the rich text content.
 *                       Each operation is an object with 'insert', 'attributes', and
 *                       optionally 'retain' or 'delete' properties.
 *
 * @returns {string} The HTML representation of the delta content.
 */
export function deserializeHTML(ops) {
  let delta = new Delta(ops);
  let htmlNodes = [];
  let prevGroupType = "";
  delta.eachLine((line, blockAttributes) => {
    if (line.ops.length === 0 && Object.keys(blockAttributes).length === 0) {
      htmlNodes.push(createElement("p"));
      prevGroupType = "";
    } else if (Object.keys(blockAttributes).length > 0) {
      let blockType = "p";
      if (blockAttributes[FORMAT_TYPES.HEADER]) {
        blockType = ASSIST_FORMAT_TYPES[blockAttributes.header];
      } else if (blockAttributes[FORMAT_TYPES.BLOCKQUOTE]) {
        blockType = FORMAT_TYPES.BLOCKQUOTE;
      } else if (
        blockAttributes.list === ASSIST_FORMAT_TYPES[FORMAT_TYPES.UL_LIST]
      ) {
        blockType = FORMAT_TYPES.UL_LIST;
      } else if (
        blockAttributes.list === ASSIST_FORMAT_TYPES[FORMAT_TYPES.OL_LIST]
      ) {
        blockType = FORMAT_TYPES.OL_LIST;
      } else if (blockAttributes[FORMAT_TYPES.CODE]) {
        blockType = FORMAT_TYPES.CODE;
      }
      const nodes =
        line.ops.length === 0
          ? [createTextNode("")]
          : deserializerObject(line.ops);

      const element = createElement(blockType);
      if (blockAttributes.align) {
        setAttribute(element, "data-align", blockAttributes.align);
        setAttribute(element, "class", `ql-align-${blockAttributes.align}`);
      }
      if (nodes.length) {
        nodes.forEach((node) => node && element.appendChild(node));
      }
      if (prevGroupType === blockType) {
        setAttribute(element, "class", `ql-${blockType}-item`);
        htmlNodes[htmlNodes.length - 1].appendChild(element);
      } else if (GROUP_TYPES.includes(blockType)) {
        const grpNode = createElement(ASSIST_GROUP_TYPE[blockType]);
        setAttribute(element, "class", `ql-${blockType}-item`);
        grpNode.appendChild(element);
        setAttribute(grpNode, "class", `ql-${blockType}`);
        htmlNodes.push(grpNode);
        prevGroupType = blockType;
      } else {
        htmlNodes.push(element);
        prevGroupType = "";
      }
    } else {
      let nodes = deserializerObject(line.ops);
      if (nodes.length) {
        let textNodes = [];
        nodes.forEach((node) => {
          if (
            node?.dataset?.type &&
            CUSTOM_BLOCK_TYPES.includes(node.dataset?.type)
          ) {
            htmlNodes.push(node);
          } else if (node) {
            textNodes.push(node);
          }
        });
        if (textNodes.length) {
          const element = createElement("p");
          textNodes.forEach((node) => element.appendChild(node));
          htmlNodes.push(element);
        }
      }
      prevGroupType = "";
    }
  });
  const htmlstr = htmlNodes.reduce((str, node) => (str += node.outerHTML), "");
  return htmlstr;
}

function deserializerObject(ops) {
  let nodes = [];
  ops.forEach((item) => {
    if (typeof item.insert === "object") {
      getObject(item, nodes);
    } else {
      getParagraphNodes(item, nodes);
    }
  });
  return nodes;
}

function getParagraphNodes(item, nodes) {
  let textNode = createTextNode(item.insert || "");
  if (!item.attributes) {
    nodes.push(textNode);
  } else {
    const marNodes = getMarkNodes(item.attributes, textNode);
    nodes.push(marNodes);
  }
}

function getObject(item, nodes) {
  const type = Object.keys(item.insert).filter(
    (key) => key !== "attributes"
  )[0];
  const element = createElement(
    CUSTOM_BLOCK_TYPES.includes(type) ? "div" : "span"
  );
  setAttribute(element, "class", `ql-${type}`);
  setAttribute(element, "data-type", type);
  setAttribute(
    element,
    `data-${type}`,
    JSON.stringify(item.insert[type] !== true ? item.insert[type] : {})
  );
  let textNode = createTextNode(item.text || "");
  if (!(type === FORMAT_TYPES.IMAGE && item.insert?.image?.loading)) {
    textNode = createTextNode((item.insert && item.insert.text) || "");
  }
  element.appendChild(textNode);
  const markNodes = item.attributes
    ? getMarkNodes(item.attributes, element)
    : element;
  nodes.push(markNodes);
}

function getMarkNodes(attributes, textNode) {
  return ATTRIBUTE_ORDER.reduceRight((childNode, mark, index) => {
    let currNode = childNode;
    if (attributes[mark]) {
      const newMarkNode = createElement(mark);
      if (mark === FORMAT_TYPES.LINK) {
        const { href, text } = attributes[mark];
        setAttribute(newMarkNode, "href", href);
        setAttribute(newMarkNode, "class", "ql-link");
        setAttribute(newMarkNode, "target", "_blank");
        setAttribute(
          newMarkNode,
          `data-${mark}`,
          JSON.stringify({ href, text })
        );
      }
      newMarkNode.appendChild(currNode);
      currNode = newMarkNode;
    }
    if (index === 0 && attributes[FORMAT_TYPES.COLOR]) {
      const color = attributes[FORMAT_TYPES.COLOR];
      if (currNode.nodeName === "#text") {
        const spanElement = createElement();
        setAttribute(spanElement, "style", `color: ${color}`);
        spanElement.appendChild(currNode);
        currNode = spanElement;
      } else {
        setAttribute(currNode, "style", `color: ${color}`);
      }
    }
    return currNode;
  }, textNode);
}
